'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useStore } from '@/hooks/useStore'
import { FiCheck } from 'react-icons/fi'

const bootSteps = [
  { text: 'INITIALIZING COR-PIX CORE ARCHITECTURE...' },
  { text: 'SYNCHRONIZING 3D HOLOGRAPHIC GEOMETRY...' },
  { text: 'CALIBRATING NEON GREEN PULSE MATRIX...' },
  { text: 'ALL SYSTEMS ONLINE — IMPACT ENGINE ACTIVE' },
]

// Boot animation is a one-time-per-tab-session flourish, not something a visitor
// should re-sit through on every internal navigation/reload.
const BOOTED_SESSION_KEY = 'corpix-booted'

function markBooted() {
  try {
    sessionStorage.setItem(BOOTED_SESSION_KEY, '1')
  } catch {
    // ignore (private browsing / storage disabled)
  }
}

export default function PreLoader() {
  const { isBooted, setBooted } = useStore()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isFinishing, setIsFinishing] = useState(false)

  // 0. Already booted earlier this tab session — skip straight past the animation.
  useEffect(() => {
    if (isBooted) return
    try {
      if (sessionStorage.getItem(BOOTED_SESSION_KEY)) {
        setBooted(true)
      }
    } catch {
      // ignore
    }
  }, [isBooted, setBooted])

  // 1. Unconditional Master Safety Net: Guaranteed transition within 3.6 seconds regardless of mobile timer throttling
  useEffect(() => {
    if (isBooted) return
    const safetyTimer = setTimeout(() => {
      setIsFinishing(true)
      setProgress(100)
      setCurrentStepIndex(3)
      setBooted(true)
      markBooted()
    }, 3600)
    return () => clearTimeout(safetyTimer)
  }, [isBooted, setBooted])

  // 2. Main Boot Progress Interval (3000ms)
  useEffect(() => {
    if (isBooted) return

    const startTime = Date.now()
    const duration = 3000 // exactly 3 seconds

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const nextProgress = Math.min(Math.floor((elapsed / duration) * 100), 100)

      setProgress(nextProgress)

      if (nextProgress >= 25 && nextProgress < 55) setCurrentStepIndex(1)
      else if (nextProgress >= 55 && nextProgress < 85) setCurrentStepIndex(2)
      else if (nextProgress >= 85) setCurrentStepIndex(3)

      if (nextProgress >= 100) {
        clearInterval(interval)
        setIsFinishing(true)
        // Direct reliable completion trigger
        setTimeout(() => {
          setBooted(true)
          markBooted()
        }, 450)
      }
    }, 25)

    return () => clearInterval(interval)
  }, [isBooted, setBooted])

  // 3. Fallback transition for isFinishing state
  useEffect(() => {
    if (isFinishing && !isBooted) {
      const timer = setTimeout(() => {
        setBooted(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isFinishing, isBooted, setBooted])

  if (isBooted) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 1.06 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-0 z-[9999] bg-[#060509] text-white flex flex-col items-center justify-between p-8 md:p-12 select-none overflow-hidden font-mono"
      >
        {/* Background Cyber Grid & Ambient Glows */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(163,230,53,0.08)_0%,transparent_70%)] pointer-events-none" />
        <div className="absolute w-[600px] h-[600px] rounded-full border border-brand-green/10 animate-[spin_30s_linear_infinite] pointer-events-none flex items-center justify-center">
          <div className="w-[450px] h-[450px] rounded-full border border-dashed border-white/10" />
        </div>

        {/* Top HUD Header */}
        <div className="w-full flex justify-between items-center z-10 text-[10px] md:text-xs tracking-widest text-slate-400">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
            <span>COR-PIX OS BOOT LOADER v0.1.0</span>
          </div>
          <button
            onClick={() => { setBooted(true); markBooted() }}
            className="text-slate-400 hover:text-brand-green transition-colors px-3 py-1 rounded border border-white/10 hover:border-brand-green/50 bg-white/5 cursor-pointer text-[10px]"
          >
            SKIP BOOT ↗
          </button>
        </div>

        {/* Center Main Boot Display */}
        <div className="flex flex-col items-center gap-7 z-10 max-w-[620px] w-full text-center">
          {/* Official Brand Logo Badge from public/NAV_LOGO.png */}
          <motion.div
            animate={
              isFinishing
                ? { scale: [1, 1.15, 1.28], filter: 'drop-shadow(0 0 45px rgba(163,230,53,0.95))' }
                : { scale: [0.98, 1.03, 0.98] }
            }
            transition={{
              duration: isFinishing ? 0.45 : 3.0,
              repeat: isFinishing ? 0 : Infinity,
              ease: 'easeInOut',
            }}
            className="px-8 py-4 rounded-2xl border border-brand-green/45 flex items-center justify-center shadow-[0_0_35px_rgba(163,230,53,0.3)] bg-[#060509]"
          >
            <img
              src="/NAV_LOGO.png"
              alt="COR-PIX Studio Logo"
              className="h-12 md:h-16 w-auto object-contain drop-shadow-[0_0_18px_rgba(163,230,53,0.65)] select-none"
            />
          </motion.div>

          {/* Stacked 2-Line Typography: Welcome to (Line 1) & large COR-PIX (Line 2) */}
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="font-display font-semibold text-slate-300 text-lg md:text-2xl tracking-wide">
              Welcome to
            </span>
            <h1 className="font-creative font-normal text-brand-green glow-text-green text-6xl md:text-7xl tracking-wider leading-none mt-1">
              COR-PIX
            </h1>
          </div>

          {/* Terminal Readout Box */}
          <div className="w-full bg-[#0e0e16]/90 border border-white/10 rounded-xl p-4 flex flex-col gap-2.5 text-left font-mono text-[11px] shadow-2xl">
            {bootSteps.slice(0, currentStepIndex + 1).map((step, idx) => {
              const isLast = idx === currentStepIndex
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex items-center gap-2.5 ${
                    isLast ? 'text-brand-green font-semibold' : 'text-slate-400'
                  }`}
                >
                  <span className="text-brand-green/70">[{idx + 1}]</span>
                  <span className="flex-1 truncate">{step.text}</span>
                  {isLast && progress < 100 ? (
                    <span className="w-1.5 h-3 bg-brand-green animate-pulse inline-block" />
                  ) : (
                    <FiCheck className="text-brand-green shrink-0" />
                  )}
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Bottom Progress Bar & Stats */}
        <div className="w-full max-w-[560px] flex flex-col gap-2 z-10">
          <div className="flex justify-between items-center text-[10px] tracking-wider text-slate-400">
            <span>MEMORY ALLOCATION: 16.4 GB OK</span>
            <span className="text-brand-green font-bold text-xs">{Math.min(progress, 100)}%</span>
          </div>

          <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden p-0.5 border border-white/10">
            <motion.div
              className="h-full bg-brand-green rounded-full shadow-[0_0_12px_rgba(163,230,53,0.8)] transition-all duration-75 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
